/**
 * AskService - AI Chat Service for Financial Q&A
 * 
 * **For AI Agents:**
 * 
 * This service handles all communication with the backend AI chat API.
 * When debugging AI chat issues, check:
 * 
 * 1. **API Configuration** - Verify `API_BASE_URL` in `utils/apiConfig.js`
 * 2. **Authentication** - Check if user token exists in localStorage
 * 3. **Network Connectivity** - Verify backend is running on port 5000
 * 4. **Timeout Settings** - Service availability checks use 10 second timeout
 * 5. **Error Logging** - All errors are logged with timestamps and context
 * 
 * **Key Methods:**
 * - `askQuestion()` - Main method for asking AI questions
 * - `isServiceAvailable()` - Checks if AI service is accessible
 * - `makeRequest()` - Core HTTP request handler with error handling
 * 
 * **Debug Mode:**
 * Enable debug logging by setting `localStorage.setItem('ask-debug', 'true')`
 * Or it automatically enables in development mode
 */

import { API_BASE_URL } from '../utils/apiConfig';

// Debug: Log the API URL being used (only in development)
if (process.env.NODE_ENV === 'development') {
  console.log('[AskService] API_BASE_URL:', API_BASE_URL);
  console.log('[AskService] REACT_APP_API_URL from env:', process.env.REACT_APP_API_URL || 'NOT SET');
  if (!process.env.REACT_APP_API_URL) {
    console.warn('[AskService] ⚠️ WARNING: REACT_APP_API_URL is not set in environment variables!');
    console.warn('[AskService] Using default URL:', API_BASE_URL);
    console.warn('[AskService] To fix: Create .env.local with: REACT_APP_API_URL=http://localhost:5000/api');
  } else if (API_BASE_URL.includes('ondigitalocean')) {
    console.warn('[AskService] ⚠️ WARNING: Using production URL in development!');
    console.warn('[AskService] Current REACT_APP_API_URL:', process.env.REACT_APP_API_URL);
    console.warn('[AskService] Make sure you have restarted the dev server after creating .env.local');
  } else {
    console.log('[AskService] ✓ Using local development URL:', API_BASE_URL);
  }
}

/**
 * **AI Agent Note:** Debug logging utility for frontend
 * 
 * Logs are only shown in development or when `ask-debug` is enabled in localStorage.
 * Use this to troubleshoot API communication issues.
 */
const debugLog = (method, message, data = {}) => {
  if (process.env.NODE_ENV === 'development' || localStorage.getItem('ask-debug') === 'true') {
    const timestamp = new Date().toISOString();
    console.log(`[ASK SERVICE DEBUG] ${timestamp}`);
    console.log(`[METHOD: ${method}] ${message}`);
    if (Object.keys(data).length > 0) {
      console.log(`[DATA]`, data);
    }
  }
};

/**
 * **AI Agent Note:** Error logging utility
 * 
 * All errors are logged with:
 * - Timestamp
 * - Method name
 * - Error details
 * - Context information
 */
const errorLog = (method, error, context = {}) => {
  const timestamp = new Date().toISOString();
  console.error(`[ASK SERVICE ERROR] ${timestamp}`);
  console.error(`[METHOD: ${method}] Error occurred`);
  console.error(`[ERROR]`, error);
  if (Object.keys(context).length > 0) {
    console.error(`[CONTEXT]`, context);
  }
};

class AskService {
  /**
   * **AI Agent Prompt:** Get authentication token from localStorage
   * 
   * **What it does:**
   * - Retrieves JWT token from localStorage
   * - Validates token expiration
   * - Removes invalid/expired tokens
   * 
   * **Returns:**
   * - `string` - Valid JWT token
   * - `null` - If no token or token is expired
   * 
   * **AI Action Items:**
   * - [ ] Check if token exists in localStorage: `localStorage.getItem('moneysmart-token')`
   * - [ ] Verify token format (should be JWT with 3 parts separated by dots)
   * - [ ] Check token expiration in payload
   */
  getAuthToken() {
    const token = localStorage.getItem('moneysmart-token');
    if (!token) {
      return null;
    }
    
    // Basic token validation
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Date.now() / 1000;
      if (payload.exp < currentTime) {
        localStorage.removeItem('moneysmart-token');
        return null;
      }
      return token;
    } catch (error) {
      localStorage.removeItem('moneysmart-token');
      return null;
    }
  }

  /**
   * **AI Agent Prompt:** Make authenticated API request
   * 
   * **What it does:**
   * - Makes HTTP requests to the backend API
   * - Automatically adds authentication token
   * - Logs all requests and responses for debugging
   * - Handles network errors gracefully
   * 
   * **Parameters:**
   * - `endpoint` (string) - API endpoint path (e.g., '/ask/question')
   * - `options` (object) - Fetch options (method, body, headers, etc.)
   * 
   * **Returns:**
   * - `Promise<object>` - API response data
   * 
   * **Throws:**
   * - Network errors if backend is unreachable
   * - HTTP errors if response status is not OK
   * 
   * **AI Action Items for Debugging:**
   * - [ ] Check `API_BASE_URL` is correct (should include `/api`)
   * - [ ] Verify authentication token is valid
   * - [ ] Check network connectivity to backend
   * - [ ] Review console logs for request/response details
   * - [ ] Check response status codes (200 = success, 4xx/5xx = error)
   */
  async makeRequest(endpoint, options = {}) {
    const requestId = Math.random().toString(36).substring(7);
    const token = this.getAuthToken();
    
    debugLog('makeRequest', `Making request to ${endpoint}`, {
      requestId,
      hasToken: !!token,
      method: options.method || 'GET',
      endpoint: `${API_BASE_URL}${endpoint}`
    });
    
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
      ...options,
    };

    const startTime = Date.now();
    
    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, config);
      const responseTime = Date.now() - startTime;
      
      debugLog('makeRequest', `Response received`, {
        requestId,
        status: response.status,
        statusText: response.statusText,
        responseTime: `${responseTime}ms`,
        ok: response.ok
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.error || errorData.message || `HTTP error! status: ${response.status}`);
        if (errorData.details) error.details = errorData.details;
        if (errorData.code) error.code = errorData.code;
        errorLog('makeRequest', error, {
          requestId,
          status: response.status,
          endpoint,
          errorData
        });
        throw error;
      }

      const data = await response.json();
      debugLog('makeRequest', 'Request successful', {
        requestId,
        responseTime: `${responseTime}ms`,
        hasData: !!data
      });
      
      return data;
    } catch (error) {
      const responseTime = Date.now() - startTime;
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        const networkError = new Error('Unable to connect to the server. Please check if the backend is running.');
        errorLog('makeRequest', networkError, {
          requestId,
          endpoint,
          responseTime: `${responseTime}ms`,
          originalError: error.message
        });
        throw networkError;
      }
      errorLog('makeRequest', error, {
        requestId,
        endpoint,
        responseTime: `${responseTime}ms`
      });
      throw error;
    }
  }

  /**
   * **AI Agent Prompt:** Ask AI a question with conversation history
   * 
   * **What it does:**
   * - Sends user question to AI backend
   * - Maintains conversation context if conversationId provided
   * - Returns AI response with conversation metadata
   * 
   * **Parameters:**
   * - `message` (string) - User's question/message
   * - `conversationId` (string|null) - Optional conversation ID for context
   * 
   * **Returns:**
   * - `Promise<object>` - Response with structure:
   *   ```javascript
   *   {
   *     success: true,
   *     data: {
   *       id: string,
   *       answer: string,
   *       conversationId: string,
   *       createdAt: string
   *     },
   *     requestId: string
   *   }
   *   ```
   * 
   * **AI Action Items for Debugging:**
   * - [ ] Verify message is not empty
   * - [ ] Check if conversationId is valid (if provided)
   * - [ ] Verify backend is running and accessible
   * - [ ] Check response time (should be < 30 seconds)
   * - [ ] Review error logs if request fails
   */
  async askQuestion(message, conversationId = null) {
    const requestId = Math.random().toString(36).substring(7);
    debugLog('askQuestion', 'Asking question', {
      requestId,
      conversationId: conversationId || 'none',
      messageLength: message?.length || 0,
      messagePreview: message?.substring(0, 100) || 'No message'
    });
    
    try {
      const startTime = Date.now();
      const response = await this.makeRequest('/ask/question', {
        method: 'POST',
        body: JSON.stringify({ message, conversationId }),
      });
      const responseTime = Date.now() - startTime;

      debugLog('askQuestion', 'Question answered', {
        requestId,
        success: response.success,
        responseTime: `${responseTime}ms`,
        answerLength: response.data?.answer?.length || 0,
        conversationId: response.data?.conversationId,
        requestIdFromServer: response.requestId
      });

      return response;
    } catch (error) {
      errorLog('askQuestion', error, {
        requestId,
        messageLength: message?.length || 0,
        conversationId
      });
      throw error;
    }
  }

  /**
   * **AI Agent Prompt:** Ask AI a quick question without conversation history
   * 
   * **What it does:**
   * - Sends standalone question (no conversation context)
   * - Useful for one-off questions
   * 
   * **Parameters:**
   * - `message` (string) - User's question
   * 
   * **Returns:**
   * - `Promise<object>` - AI response
   */
  async askQuickQuestion(message) {
    try {
      const response = await this.makeRequest('/ask/quick', {
        method: 'POST',
        body: JSON.stringify({ message }),
      });

      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * **AI Agent Prompt:** Get conversation history
   * 
   * **What it does:**
   * - Retrieves paginated conversation history
   * - Can filter by specific conversationId
   * - Returns empty history if user is not authenticated (graceful degradation)
   * 
   * **Parameters:**
   * - `page` (number) - Page number (default: 1)
   * - `limit` (number) - Items per page (default: 20)
   * - `conversationId` (string|null) - Optional filter by conversation
   * 
   * **Returns:**
   * - `Promise<object>` - History with messages and pagination
   * 
   * **AI Action Items:**
   * - [ ] Check if user is authenticated
   * - [ ] Verify pagination parameters are valid
   * - [ ] Handle empty history gracefully (not an error)
   */
  async getHistory(page = 1, limit = 20, conversationId = null) {
    try {
      const token = this.getAuthToken();
      if (!token) {
        // Return empty history instead of throwing error
        return {
          success: true,
          data: {
            messages: [],
            pagination: {
              page: 1,
              limit: limit,
              total: 0,
              pages: 0
            }
          }
        };
      }
      
      let url = `/ask/history?page=${page}&limit=${limit}`;
      if (conversationId) {
        url += `&conversationId=${conversationId}`;
      }
      
      const response = await this.makeRequest(url);
      return response;
    } catch (error) {
      // Return empty history on error instead of throwing
      return {
        success: false,
        data: {
          messages: [],
          pagination: {
            page: 1,
            limit: limit,
            total: 0,
            pages: 0
          }
        },
        error: error.message
      };
    }
  }

  /**
   * **AI Agent Prompt:** Create a new conversation
   * 
   * **What it does:**
   * - Creates a new conversation thread
   * - AI can auto-generate title from first message if title not provided
   * 
   * **Parameters:**
   * - `title` (string|null) - Optional conversation title
   * - `message` (string|null) - Optional first message (used to generate title)
   * 
   * **Returns:**
   * - `Promise<object>` - New conversation with ID
   * 
   * **AI Action Items:**
   * - [ ] Verify user is authenticated
   * - [ ] Check if conversation was created successfully
   * - [ ] Store conversationId for subsequent messages
   */
  async createConversation(title = null, message = null) {
    try {
      const response = await this.makeRequest('/ask/conversations', {
        method: 'POST',
        body: JSON.stringify({ title, message }),
      });
      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * **AI Agent Prompt:** Get all conversations for the user
   * 
   * **What it does:**
   * - Retrieves list of all user's conversations
   * - Used for conversation sidebar/listing
   * 
   * **Returns:**
   * - `Promise<object>` - Array of conversations
   * 
   * **AI Action Items:**
   * - [ ] Verify user is authenticated
   * - [ ] Handle empty conversations list (user has no conversations yet)
   */
  async getConversations() {
    try {
      const response = await this.makeRequest('/ask/conversations');
      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * **AI Agent Prompt:** Get a specific conversation with its messages
   * 
   * **What it does:**
   * - Retrieves full conversation details including all messages
   * 
   * **Parameters:**
   * - `conversationId` (string) - Conversation ID to retrieve
   * 
   * **Returns:**
   * - `Promise<object>` - Conversation with messages
   */
  async getConversation(conversationId) {
    try {
      const response = await this.makeRequest(`/ask/conversations/${conversationId}`);
      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * **AI Agent Prompt:** Update conversation title
   * 
   * **What it does:**
   * - Updates the title of an existing conversation
   * 
   * **Parameters:**
   * - `conversationId` (string) - Conversation ID to update
   * - `title` (string) - New title
   * 
   * **Returns:**
   * - `Promise<object>` - Updated conversation
   */
  async updateConversation(conversationId, title) {
    try {
      const response = await this.makeRequest(`/ask/conversations/${conversationId}`, {
        method: 'PUT',
        body: JSON.stringify({ title }),
      });
      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * **AI Agent Prompt:** Delete a conversation
   * 
   * **What it does:**
   * - Permanently deletes a conversation and all its messages
   * 
   * **Parameters:**
   * - `conversationId` (string) - Conversation ID to delete
   * 
   * **Returns:**
   * - `Promise<object>` - Deletion confirmation
   */
  async deleteConversation(conversationId) {
    try {
      const response = await this.makeRequest(`/ask/conversations/${conversationId}`, {
        method: 'DELETE',
      });
      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * **AI Agent Prompt:** Get suggested questions
   * 
   * **What it does:**
   * - Retrieves suggested questions for users (no auth required)
   * - Used for quick-start prompts in the UI
   * 
   * **Returns:**
   * - `Promise<object>` - Array of suggested questions
   * 
   * **AI Action Items:**
   * - [ ] Handle gracefully if suggestions endpoint fails
   * - [ ] Can use fallback suggestions if API unavailable
   */
  async getSuggestions() {
    try {
      const response = await fetch(`${API_BASE_URL}/ask/suggestions`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  /**
   * **AI Agent Prompt:** Clear conversation history
   * 
   * **What it does:**
   * - Deletes all conversation history for the user
   * 
   * **Returns:**
   * - `Promise<object>` - Deletion confirmation
   * 
   * **AI Action Items:**
   * - [ ] Verify user wants to delete all history (destructive action)
   * - [ ] Confirm deletion was successful
   */
  async clearHistory() {
    try {
      const response = await this.makeRequest('/ask/history', {
        method: 'DELETE',
      });
      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * **AI Agent Prompt:** Get AI service status
   * 
   * **What it does:**
   * - Checks if AI service is available and configured
   * - Returns service health information
   * 
   * **Returns:**
   * - `Promise<object>` - Status with availability, model info, API key status
   * 
   * **AI Action Items:**
   * - [ ] Check if service is available
   * - [ ] Verify API key is configured
   * - [ ] Check which AI model is being used
   */
  async getStatus() {
    try {
      const response = await this.makeRequest('/ask/status');
      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * **AI Agent Prompt:** Get usage statistics
   * 
   * **What it does:**
   * - Retrieves AI service usage statistics
   * - Can filter by date range
   * 
   * **Parameters:**
   * - `startDate` (string|null) - Optional start date filter
   * - `endDate` (string|null) - Optional end date filter
   * 
   * **Returns:**
   * - `Promise<object>` - Usage statistics
   */
  async getStats(startDate = null, endDate = null) {
    try {
      let url = '/ask/stats';
      const params = new URLSearchParams();
      
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await this.makeRequest(url);
      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * **AI Agent Prompt:** Check if AI service is available
   * 
   * **What it does:**
   * - Checks if AI backend service is accessible (no auth required)
   * - Uses 10 second timeout for availability checks
   * - Tries `/ask/status` endpoint first, falls back to `/health` endpoint
   * - Returns `false` on timeout or error (graceful degradation)
   * 
   * **Returns:**
   * - `Promise<boolean>` - `true` if service is available, `false` otherwise
   * 
   * **AI Action Items for Debugging Timeout Errors:**
   * - [ ] Verify backend is running on port 5000
   * - [ ] Check if `/ask/status` endpoint is accessible
   * - [ ] Verify network connectivity (no firewall blocking)
   * - [ ] Check timeout setting (currently 10 seconds)
   * - [ ] Review console logs for specific error details
   * - [ ] Test endpoint manually: `curl http://localhost:5000/api/ask/status`
   * 
   * **Common Issues:**
   * - **Timeout Error:** Backend not running or slow to respond
   * - **Network Error:** Backend unreachable or wrong URL
   * - **CORS Error:** Backend CORS configuration issue
   */
  async isServiceAvailable() {
    const requestId = Math.random().toString(36).substring(7);
    debugLog('isServiceAvailable', 'Checking service availability', { requestId });
    
    // **AI Agent Note:** Timeout increased to 10 seconds for availability checks
    // This gives backend more time to respond, especially on slow networks
    const TIMEOUT_MS = 10000;
    
    try {
      // First try the /ask/status endpoint which returns AI-specific status
      const statusUrl = `${API_BASE_URL}/ask/status`;
      debugLog('isServiceAvailable', 'Checking AI status endpoint', { requestId, url: statusUrl });
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);
      
      try {
        const response = await fetch(statusUrl, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        
        if (response.ok) {
          const status = await response.json();
          const isAvailable = status.success && status.data?.available === true;
          
          debugLog('isServiceAvailable', 'Service status retrieved', {
            requestId,
            available: isAvailable,
            model: status.data?.model,
            hasApiKey: status.data?.hasApiKey
          });
          
          return isAvailable;
        }
        
        debugLog('isServiceAvailable', 'Status endpoint returned error', {
          requestId,
          status: response.status,
          statusText: response.statusText
        });
      } catch (fetchError) {
        clearTimeout(timeoutId);
        if (fetchError.name === 'AbortError') {
          debugLog('isServiceAvailable', 'Status endpoint request timed out', { requestId });
        } else {
          throw fetchError;
        }
      }
      
      // Fallback to general health check
      const healthUrl = `${API_BASE_URL.replace('/api', '')}/health`;
      debugLog('isServiceAvailable', 'Fallback to health endpoint', { requestId, url: healthUrl });
      
      const healthController = new AbortController();
      const healthTimeoutId = setTimeout(() => healthController.abort(), TIMEOUT_MS);
      
      try {
        const healthResponse = await fetch(healthUrl, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          signal: healthController.signal
        });
        
        clearTimeout(healthTimeoutId);
        
        if (healthResponse.ok) {
          const health = await healthResponse.json();
          const isAvailable = health.status === 'OK';
          
          debugLog('isServiceAvailable', 'Health check completed', {
            requestId,
            available: isAvailable,
            status: health.status
          });
          
          return isAvailable;
        }
      } catch (healthError) {
        clearTimeout(healthTimeoutId);
        if (healthError.name === 'AbortError') {
          debugLog('isServiceAvailable', 'Health check request timed out', { requestId });
        }
      }
      
      return false;
    } catch (error) {
      if (error.name === 'AbortError' || error.message?.includes('timeout')) {
        debugLog('isServiceAvailable', 'Service availability check timed out', { 
          requestId,
          timeout: `${TIMEOUT_MS}ms`
        });
      } else {
        errorLog('isServiceAvailable', error, { requestId });
      }
      return false;
    }
  }

  /**
   * **AI Agent Prompt:** Get debug information about the AI service
   * 
   * **What it does:**
   * - Retrieves detailed debug information from backend
   * - Useful for troubleshooting AI service issues
   * 
   * **Returns:**
   * - `Promise<object>` - Debug information
   * 
   * **AI Action Items:**
   * - [ ] Use this for detailed troubleshooting
   * - [ ] Check API key configuration
   * - [ ] Verify model availability
   * - [ ] Review service configuration
   */
  async getDebugInfo() {
    const requestId = Math.random().toString(36).substring(7);
    debugLog('getDebugInfo', 'Fetching debug information', { requestId });
    
    try {
      const response = await fetch(`${API_BASE_URL}/ask/debug`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        signal: AbortSignal.timeout(10000)
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const debugInfo = await response.json();
      debugLog('getDebugInfo', 'Debug info retrieved', { requestId, debugInfo });
      
      return debugInfo;
    } catch (error) {
      errorLog('getDebugInfo', error, { requestId });
      throw error;
    }
  }
}

/**
 * **AI Agent Note:** Service Export
 * 
 * AskService is exported as a singleton instance.
 * Import and use like this:
 * 
 * ```javascript
 * import AskService from './services/AskService';
 * 
 * // Check if service is available
 * const isAvailable = await AskService.isServiceAvailable();
 * 
 * // Ask a question
 * const response = await AskService.askQuestion('What is a stock?', conversationId);
 * ```
 * 
 * **For AI Agents Debugging:**
 * 
 * When troubleshooting AskService issues:
 * 
 * 1. **Enable Debug Mode:**
 *    ```javascript
 *    localStorage.setItem('ask-debug', 'true');
 *    ```
 * 
 * 2. **Check API Configuration:**
 *    - Verify `API_BASE_URL` in `utils/apiConfig.js`
 *    - Should be `http://localhost:5000/api` in development
 * 
 * 3. **Verify Backend:**
 *    - Backend must be running on port 5000
 *    - Test: `curl http://localhost:5000/api/ask/status`
 * 
 * 4. **Check Authentication:**
 *    - Token must exist: `localStorage.getItem('moneysmart-token')`
 *    - Token must not be expired
 * 
 * 5. **Review Console Logs:**
 *    - All requests/responses are logged with timestamps
 *    - Errors include full context for debugging
 */
export default new AskService();
